import React, { useState, useEffect } from 'react';
import { cmp } from '@guardian/consent-management-platform';
import { canShow as canShowRRBanner, ReaderRevenueBanner, } from '@root/src/web/components/StickyBottomBanner/ReaderRevenueBanner';
import { getAlreadyVisitedCount } from '@root/src/web/lib/alreadyVisited';
import { pickBanner } from './bannerPicker';
import { BrazeBanner, canShow as canShowBrazeBanner } from './BrazeBanner';
const getBannerLastClosedAt = (key) => {
    const item = localStorage.getItem(`gu.prefs.${key}`);
    return (item && JSON.parse(item).value) || undefined;
};
const DEFAULT_BANNER_TIMEOUT_MILLIS = 2000;
const buildCmpBannerConfig = () => ({
    id: 'cmpUi',
    canShow: () => cmp.willShowPrivacyMessage().then((result) => ({ result: !!result })),
    show: () => {
        // New CMP is not a react component and is shown outside of react's world
        // so render nothing if it will show
        return null;
    },
    timeoutMillis: null,
});
const buildReaderRevenueBannerConfig = (CAPI, isSignedIn, asyncCountryCode) => {
    return {
        id: 'reader-revenue-banner',
        canShow: () => canShowRRBanner({
            remoteBannerConfig: CAPI.config.remoteBanner,
            isSignedIn,
            asyncCountryCode,
            contentType: CAPI.contentType,
            sectionName: CAPI.sectionName,
            shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue,
            isMinuteArticle: CAPI.pageType.isMinuteArticle,
            isPaidContent: CAPI.pageType.isPaidContent,
            isSensitive: CAPI.config.isSensitive,
            tags: CAPI.tags,
            contributionsServiceUrl: CAPI.contributionsServiceUrl,
            alreadyVisitedCount: getAlreadyVisitedCount(),
            engagementBannerLastClosedAt: getBannerLastClosedAt('engagementBannerLastClosedAt'),
            subscriptionBannerLastClosedAt: getBannerLastClosedAt('subscriptionBannerLastClosedAt'),
            switches: {
                remoteSubscriptionsBanner: !!CAPI.config
                    .remoteSubscriptionsBanner,
            },
        }),
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        show: (meta) => () => React.createElement(ReaderRevenueBanner, Object.assign({}, meta)),
        timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
    };
};
const buildBrazeBanner = (asyncBrazeUuid, isDigitalSubscriber) => ({
    id: 'braze-banner',
    canShow: () => canShowBrazeBanner(asyncBrazeUuid, isDigitalSubscriber),
    show: (meta) => () => React.createElement(BrazeBanner, { meta: meta }),
    timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
});
const StickyBottomBannerWithFullfilledDependencies = ({ isSignedIn, asyncCountryCode, CAPI, asyncBrazeUuid, isDigitalSubscriber, }) => {
    const [SelectedBanner, setSelectedBanner] = useState(null);
    useEffect(() => {
        const CMP = buildCmpBannerConfig();
        const readerRevenue = buildReaderRevenueBannerConfig(CAPI, isSignedIn, asyncCountryCode);
        const brazeBanner = buildBrazeBanner(asyncBrazeUuid, isDigitalSubscriber);
        const bannerConfig = [CMP, readerRevenue, brazeBanner];
        pickBanner(bannerConfig).then((PickedBanner) => setSelectedBanner(PickedBanner));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array because we only want this to run once
    if (SelectedBanner) {
        return React.createElement(SelectedBanner, null);
    }
    return null;
};
// This outer component exists because we don't want to run the banner picker
// until all of our dependencies are defined. Then when they are all defined we
// only want to run the banner picker once.
export const StickyBottomBanner = ({ isSignedIn, asyncCountryCode, CAPI, asyncBrazeUuid, isDigitalSubscriber, }) => {
    if (isSignedIn === undefined ||
        asyncCountryCode === undefined ||
        asyncBrazeUuid === undefined ||
        isDigitalSubscriber === undefined) {
        return null;
    }
    return (React.createElement(StickyBottomBannerWithFullfilledDependencies, { isSignedIn: isSignedIn, asyncCountryCode: asyncCountryCode, asyncBrazeUuid: asyncBrazeUuid, isDigitalSubscriber: isDigitalSubscriber, CAPI: CAPI }));
};
